import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  InputAdornment,
  CircularProgress,
  Box,
} from "@mui/material";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../../../../context";
import LoadingButton from "@mui/lab/LoadingButton";
import useFetchObjects from "../../../../../api/useFetchObjects";
import useAdd from "../../../../../api/useAdd";
import { useTranslation } from "react-i18next";
import TicketSearch from "../../../../../Components/TicketSearch";
import TicketSelectionView from "../../../../../Components/TicketSelectionView";

const AddAppointment = ({
  open,
  onClose,
  initialTicket = null,
  initialPatient = null,
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const user = auth?.user;
  const token = user?.token;

  // Utility function to get the current time and date
  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getCurrentTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    ticket: initialTicket,
    patient: initialPatient || initialTicket?.patient || null,
    doctor: "",
    sickness_history: "",
    timeOfVisit: getCurrentTime(),
    dateOfVisit: getCurrentDate(),
    due: "",
    fees: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [doctorSearchTerm, setDoctorSearchTerm] = useState("");
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const doctorSearchDebounceRef = useRef(null);

  // Doctor (treating physician) list: always filter by doctor=true; optionally by ticket's department
  const ticketDeptId =
    initialTicket?.department?.id ?? initialTicket?.department ?? null;
  const doctorsQueryParams = new URLSearchParams({
    doctor: "true",
    page_size: "25",
    search: doctorSearchTerm || "",
  });
  if (ticketDeptId != null) {
    doctorsQueryParams.set("department", String(ticketDeptId));
  }
  const doctorsUrl = `users/?${doctorsQueryParams.toString()}`;
  const { data: doctorsData, isLoading: isLoadingDoctors } = useFetchObjects(
    ["DoctorsList", doctorSearchTerm, ticketDeptId],
    doctorsUrl,
    token,
    { enabled: open },
  );
  const doctors = doctorsData?.results ?? doctorsData ?? [];

  // Resolve selected doctor object from formData.doctor (id) when list loads
  const doctorId = formData.doctor != null ? Number(formData.doctor) : null;
  const resolvedDoctor =
    selectedDoctor ??
    (doctorId != null
      ? doctors.find((d) => Number(d.id) === doctorId)
      : null) ??
    null;

  const handleDoctorInputChange = useCallback((value) => {
    if (doctorSearchDebounceRef.current)
      clearTimeout(doctorSearchDebounceRef.current);
    doctorSearchDebounceRef.current = setTimeout(() => {
      setDoctorSearchTerm(value ?? "");
      doctorSearchDebounceRef.current = null;
    }, 300);
  }, []);

  useEffect(() => {
    return () => {
      if (doctorSearchDebounceRef.current)
        clearTimeout(doctorSearchDebounceRef.current);
    };
  }, []);

  // Update form when initialTicket or initialPatient changes
  useEffect(() => {
    if (open && (initialTicket || initialPatient)) {
      setFormData((prev) => ({
        ...prev,
        ticket: initialTicket || prev.ticket,
        patient: initialPatient || initialTicket?.patient || prev.patient,
      }));
    }
  }, [open, initialTicket, initialPatient]);

  // Handle ticket selection
  const handleTicketChange = (ticket) => {
    if (ticket) {
      setFormData({
        ...formData,
        ticket: ticket,
        patient: ticket.patient, // Set patient from ticket
      });
    } else {
      setFormData({
        ...formData,
        ticket: null,
        patient: null,
      });
    }
  };

  const { handleAdd } = useAdd("appointments", token);

  const handleSubmit = (e) => {
    e.preventDefault();
    let errors = {};
    Object.keys(formData).forEach((key) => {
      if (
        !formData[key] &&
        key !== "patient" &&
        key !== "ticket" &&
        key !== "due"
      ) {
        errors[key] = t("reception.requiredField");
      }
    });

    if (!formData.ticket) {
      errors.ticket = t("reception.requiredField", {
        defaultValue: "Ticket is required",
      });
    }

    if (!formData.patient) {
      errors.patient = t("reception.requiredField");
    }

    if (Object.keys(errors).length === 0) {
      const appointmentData = new FormData();
      Object.keys(formData).forEach((key) => {
        if (key === "patient") {
          appointmentData.append(key, formData[key]?.id || formData[key]);
        } else if (key === "ticket") {
          appointmentData.append(key, formData[key]?.id || "");
        } else {
          appointmentData.append(key, formData[key]);
        }
      });
      handleAdd(appointmentData);
      onClose();
    } else {
      setFormErrors(errors);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setFormErrors({ ...formErrors, [e.target.name]: "" });
  };

  // Reset form and doctor search when dialog is closed (unless initial values are provided)
  useEffect(() => {
    if (!open) {
      setDoctorSearchTerm("");
      setSelectedDoctor(null);
      if (doctorSearchDebounceRef.current) {
        clearTimeout(doctorSearchDebounceRef.current);
        doctorSearchDebounceRef.current = null;
      }
    }
    if (!open && !initialTicket && !initialPatient) {
      setFormData({
        ticket: null,
        patient: null,
        doctor: "",
        sickness_history: "",
        timeOfVisit: getCurrentTime(),
        dateOfVisit: getCurrentDate(),
        due: "",
        fees: "",
      });
      setFormErrors({});
    }
  }, [open, initialTicket, initialPatient]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <form onSubmit={handleSubmit}>
        <DialogTitle
          style={{
            backgroundColor: "#3f51b5",
            color: "white",
            textAlign: "center",
          }}
        >
          <Typography variant="h6">{t("reception.createOPD")}</Typography>
        </DialogTitle>
        <DialogContent style={{ padding: "2rem" }}>
          <Grid spacing={4} container>
            {/* Ticket Search - Only show if no initial ticket provided */}
            {!initialTicket && (
              <Grid xs={12} item>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, fontWeight: "bold" }}
                >
                  {t("reception.searchByTicket", {
                    defaultValue: "Search by Ticket Number",
                  })}
                </Typography>
                <TicketSearch
                  value={formData.ticket}
                  onChange={handleTicketChange}
                  placeholder={t("reception.searchTicketPlaceholder", {
                    defaultValue: "Search by ticket number...",
                  })}
                  label={t("reception.ticketNumber", {
                    defaultValue: "Ticket Number",
                  })}
                />
              </Grid>
            )}

            {/* Patient Summary View */}
            {formData.ticket && (
              <Grid xs={12} item>
                <TicketSelectionView ticket={formData.ticket} />
              </Grid>
            )}

            {/* Hidden patient field (auto-filled from ticket) */}
            {formData.patient && (
              <input type="hidden" name="patient" value={formData.patient} />
            )}
            {/* Doctor (treating physician) selection */}
            <Grid xs={12} md={6} item>
              <Autocomplete
                options={doctors}
                getOptionLabel={(option) =>
                  option
                    ? `${option.first_name ?? ""} ${option.last_name ?? ""}`.trim() || ""
                    : ""
                }
                value={resolvedDoctor}
                onInputChange={(_, value) => handleDoctorInputChange(value)}
                onChange={(_, newValue) => {
                  setSelectedDoctor(newValue);
                  setFormData((prev) => ({
                    ...prev,
                    doctor: newValue ? newValue.id : "",
                  }));
                  setFormErrors((prev) => ({ ...prev, doctor: "" }));
                }}
                filterOptions={(options) => options}
                loading={isLoadingDoctors}
                isOptionEqualToValue={(option, value) =>
                  option != null && value != null && Number(option.id) === Number(value.id)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={t("reception.doctor")}
                    variant="outlined"
                    error={!!formErrors.doctor}
                    helperText={formErrors.doctor}
                    required
                    placeholder={t("reception.search", {
                      defaultValue: "Search...",
                    })}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingDoctors ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid xs={12} item>
              <TextField
                autoFocus
                size="small"
                label={t("reception.sickness_history")}
                name="sickness_history"
                type="text"
                fullWidth
                value={formData.sickness_history}
                onChange={handleChange}
                variant="outlined"
                multiline
                rows={2}
                error={!!formErrors.sickness_history}
                helperText={formErrors.sickness_history}
                required
              />
            </Grid>
            <Grid xs={12} md={6} item>
              <TextField
                autoFocus
                size="small"
                label={t("reception.timeOfVisit")}
                name="timeOfVisit"
                type="time"
                fullWidth
                onChange={handleChange}
                value={formData.timeOfVisit}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </Grid>
            <Grid xs={12} md={6} item>
              <TextField
                autoFocus
                size="small"
                label={t("reception.dateOfVisit")}
                name="dateOfVisit"
                type="date"
                fullWidth
                onChange={handleChange}
                value={formData.dateOfVisit}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </Grid>
            <Grid xs={12} md={6} item>
              <TextField
                autoFocus
                size="small"
                label={t("reception.due")}
                name="due"
                type="number"
                fullWidth
                value={formData.due}
                onChange={handleChange}
                variant="outlined"
                error={!!formErrors.due}
                helperText={formErrors.due}
                required
              />
            </Grid>
            <Grid xs={12} md={6} item>
              <TextField
                autoFocus
                size="small"
                label={t("reception.fees")}
                name="fees"
                type="number"
                fullWidth
                value={formData.fees}
                onChange={handleChange}
                variant="outlined"
                error={!!formErrors.fees}
                helperText={formErrors.fees}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          style={{ justifyContent: "center", paddingBottom: "20px" }}
        >
          <Button
            onClick={onClose}
            color="primary"
            variant="outlined"
            style={{ margin: "0px 10px" }}
          >
            {t("reception.cancel")}
          </Button>
          <LoadingButton type="submit" color="primary" variant="contained">
            {t("reception.save")}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddAppointment;
